import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Check, Compass, UserRound, ArrowRight, Loader2 } from "lucide-react";

const PACKAGES = [
  {
    testid: "package-card-diy",
    ctaTestid: "package-cta-diy",
    icon: Compass,
    name: "Do-It-Yourself System",
    price: "$97",
    lookup_key: "diy_monthly",
    blurb:
      "Get the complete Life Mastery System and work it at your own pace — the full map, without the 1-on-1 price tag.",
    features: [
      "Complete step-by-step Life Mastery System",
      "Self-paced lessons, worksheets, and templates",
      "Goal clarity exercises and weekly action planners",
      "New tools and lessons added regularly",
    ],
    cta: "Start at $97/mo",
    featured: false,
  },
  {
    testid: "package-card-1on1",
    ctaTestid: "package-cta-1on1",
    icon: UserRound,
    name: "One-on-One Coaching",
    price: "$997",
    lookup_key: "coaching_1on1_monthly",
    blurb:
      "Private, personalized coaching for people ready to move fast, go deep, and build their next chapter with direct support.",
    features: [
      "Private coaching sessions directly with me",
      "A fully personalized mastery plan for your life",
      "Direct access to me between sessions",
      "Priority support and faster feedback loops",
    ],
    cta: "Apply for 1-on-1",
    featured: true,
  },
];

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CoachingPackages() {
  const [loading, setLoading] = useState(null);

  const startCheckout = async (lookupKey) => {
    setLoading(lookupKey);
    try {
      const { data } = await axios.post(`${API}/payments/checkout`, {
        lookup_key: lookupKey,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error("Couldn't start checkout. Please try again.");
      setLoading(null);
    }
  };
  return (
    <section
      id="packages"
      data-testid="packages-section"
      className="scroll-mt-20 bg-white py-16 sm:py-24"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-2xl text-center">
          <h2
            data-testid="packages-heading"
            className="font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl"
          >
            Work Directly With John
          </h2>
          <p className="mt-4 text-base leading-relaxed text-slate-600 sm:text-lg">
            Two ways to start. Same system, same mission — pick the level of support that fits your
            season.
          </p>
        </div>

        <div className="mx-auto mt-12 grid max-w-4xl gap-8 md:grid-cols-2">
          {PACKAGES.map((p, i) => (
            <motion.div
              key={p.testid}
              data-testid={p.testid}
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.55, delay: i * 0.12 }}
              className={`relative flex flex-col rounded-3xl bg-white p-8 transition-all duration-300 hover:-translate-y-1.5 hover:shadow-xl hover:shadow-blue-900/10 ${
                p.featured
                  ? "shadow-lg shadow-blue-900/10 ring-2 ring-blue-700"
                  : "border border-slate-200 shadow-sm"
              }`}
            >
              {p.featured && (
                <span className="absolute -top-3.5 left-1/2 -translate-x-1/2 rounded-full bg-blue-700 px-4 py-1 text-[11px] font-bold uppercase tracking-wide text-white shadow-md">
                  Most Personal
                </span>
              )}
              <div className="flex items-center gap-4">
                <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-700/10 text-blue-700">
                  <p.icon className="h-6 w-6" />
                </span>
                <h3 className="font-display text-xl font-bold text-slate-900">{p.name}</h3>
              </div>
              <p className="mt-5">
                <span className="font-display text-4xl font-extrabold tracking-tight text-slate-900">
                  {p.price}
                </span>
                <span className="text-sm font-semibold text-slate-500"> /month</span>
              </p>
              <p className="mt-3 text-sm leading-relaxed text-slate-600">{p.blurb}</p>
              <ul className="mt-6 flex-1 space-y-3">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2.5 text-sm text-slate-700">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" />
                    {f}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => startCheckout(p.lookup_key)}
                disabled={loading !== null}
                data-testid={p.ctaTestid}
                className={`mt-8 inline-flex items-center justify-center gap-2 rounded-xl px-5 py-3.5 text-sm font-bold transition-all duration-200 hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-70 ${
                  p.featured
                    ? "bg-blue-700 text-white shadow-lg shadow-blue-700/25 hover:bg-blue-800 hover:shadow-xl"
                    : "border-2 border-blue-700 text-blue-700 hover:bg-blue-700 hover:text-white"
                }`}
              >
                {loading === p.lookup_key ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    {p.cta}
                    <ArrowRight className="h-4 w-4" />
                  </>
                )}
              </button>
            </motion.div>
          ))}
        </div>

        <p className="mt-10 text-center text-sm text-slate-500">
          Not sure which fits? Grab the free eBook above and start there — no pressure either way.
        </p>
      </div>
    </section>
  );
}
